/**--external-- */
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

/**--relative-- */
import classes from "./KonvaContextMenu.scss";
import { maintainElementContextMenu } from "./KonvaContextMenuUtils";
import { KONVA_ATTR, KONVA_EVENTS } from "../Konva/KonvaConstants";

const copyIds = {};

/**
 *
 * @param {  konva, children, }
 * @returns Context Menu Component for konva Elements
 */
const KonvaContextMenu = ({ konva, fieldId, contextMenu, ...rest }) => {
  const [node, setNode] = useState(null);
  const [resetter, setResetter] = useState(0);
  const contextRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = event => {
      const key = event.key;
      const nodeId = node ? node.id() : null;

      // * Prenventing keyboard action, if text edit in-progress
      const isChangingText = konva?.stage?.getAttr(KONVA_ATTR.IS_CHANGING_TEXT);
      if (isChangingText) return;

      if (nodeId && (key === "Delete" || key === "Backspace")) {
        // * Remove element
        _.get(rest, "onRemove", () => {})(nodeId);
      } else if (event.ctrlKey || event.metaKey) {
        // * keyboard actions with cmd or ctrl
        if (key === "d" && nodeId) {
          // * Duplicate element with Ctrl+D or Cmd+D
          event?.preventDefault?.();
          _.get(rest, "onDuplicate", () => {})(nodeId);
        } else if (key === "v" && nodeId && copyIds[fieldId]) {
          // * Paste element with Ctrl+V or Cmd+V
          _.get(rest, "onDuplicate", () => {})(copyIds[fieldId]);
        } else if (key === "c" && nodeId) {
          // * Copy element with Ctrl+C or Cmd+C
          copyIds[fieldId] = nodeId;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [node]);

  // * detaching transfomer on clicking outside stage
  useEffect(() => {
    if (!konva || !_.get(konva, "stage") || !_.get(konva, "transformer")) {
      return;
    }

    const transformer = _.get(konva, "transformer");
    const handleClickOutside = e => {
      // * if click is outside stage, detaching transformer and firing event
      if (konva.stage?.container()?.contains(e.target)) return;
      if (contextRef.current && contextRef.current.contains(e.target)) return;

      // * Check for konva text editor
      const konvaTexEditAttr = '[data-context-id="konva-text-editor"]';
      const knvaTextEdit = document.querySelector(konvaTexEditAttr);
      if (knvaTextEdit && knvaTextEdit.contains?.(e.target)) return;

      // * Check for warning button icon
      const dropzoneWarningAttr =
        '[data-context-id="drag-and-drop-question-warning-icon-container"]';
      const dropzoneWarning = document.querySelector(dropzoneWarningAttr);
      if (dropzoneWarning && dropzoneWarning.contains?.(e.target)) {
        // * if clicked on warning dropzone, open dropzone in one-click, by preventing re-draw
        transformer.nodes([]);
        contextRef.current.style.display = "none";
        return;
      }

      transformer.nodes([]);
      transformer.fire(KONVA_EVENTS.TRANSFORMERDETACHED, { evt: null }, true);
    };

    window.addEventListener("pointerdown", handleClickOutside);
    return () => {
      window.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [konva]);

  useMemo(() => {
    if (!contextRef.current) {
      if (resetter > 20) return;

      // * Waiting for dom to setup
      setTimeout(() => setResetter(resetter + 1), 500);
      return;
    }
    maintainElementContextMenu(konva, contextRef, setNode);
  }, [konva, resetter, contextRef]);

  return (
    <div ref={contextRef} className={classes.konvaContextMenu}>
      {node && _.isFunction(contextMenu) && contextMenu(node)}
    </div>
  );
};

export default KonvaContextMenu;

KonvaContextMenu.defaultProps = {
  konva: null,
  contextMenu: null,
};

KonvaContextMenu.propTypes = {
  konva: PropTypes.any,
  contextMenu: PropTypes.any,
};
